import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { AlertTriangle, Loader2, TrendingDown, TrendingUp } from "lucide-react";
import { Badge } from "./ui/badge";
import { Card, CardContent } from "./ui/card";
import { Input } from "./ui/input";
import { Label } from "./ui/label";

const API_URL = process.env.REACT_APP_BACKEND_URL;
const LABELS = {
  alimentari: "Alimentari",
  igiene: "Detersivi e igiene",
  farmaci: "Farmaci",
  utenze: "Utenze",
  manutenzioni: "Manutenzioni",
  altro: "Altro",
};

export default function FatturazioneControlloCostiTab({ headers, formatEuro, strutturaIdForConfig }) {
  const [mese, setMese] = useState(() => new Date().toISOString().slice(0, 7));
  const [soglia, setSoglia] = useState(20);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [uncategorized, setUncategorized] = useState([]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/api/fatturazione/controllo-costi`, {
        headers,
        params: { mese, soglia_anomalia: soglia, strutturaId: strutturaIdForConfig || undefined },
      });
      setData(response.data);
      const archive = await axios.get(`${API_URL}/api/fatturazione/fatture-passive/archivio`, {
        headers,
        params: { mese, per_page: 50, strutturaId: strutturaIdForConfig || undefined },
      });
      setUncategorized((archive.data?.items || []).filter((item) => !item.categoria_costo));
    } finally {
      setLoading(false);
    }
  }, [headers, mese, soglia, strutturaIdForConfig]);

  useEffect(() => { load().catch(() => setData(null)); }, [load]);

  const setCategory = async (item, categoria_costo) => {
    try {
      await axios.patch(
        `${API_URL}/api/fatturazione/fatture-passive/archivio/${item.id}`,
        { categoria_costo },
        { headers },
      );
      await load();
    } catch {
      // Keep the previous selection and let the row remain visible.
    }
  };

  if (loading && !data) {
    return <div className="p-10 text-center text-slate-500"><Loader2 className="w-5 h-5 animate-spin mx-auto mb-2" />Caricamento controllo costi…</div>;
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row sm:items-end gap-3">
        <div>
          <h2 className="text-lg font-semibold text-slate-900">Controllo costi fornitori</h2>
          <p className="text-sm text-slate-500">Fatture passive per categoria e confronto con la media degli ultimi sei mesi.</p>
        </div>
        <div className="flex gap-2 sm:ml-auto">
          <div><Label className="text-xs">Mese</Label><Input type="month" value={mese} onChange={(e) => setMese(e.target.value)} /></div>
          <div><Label className="text-xs">Soglia anomalia</Label><Input type="number" min="1" max="500" value={soglia} onChange={(e) => setSoglia(Number(e.target.value) || 20)} /></div>
        </div>
      </div>
      {data && (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <Card><CardContent className="p-4"><p className="text-xs text-slate-500">Costi del mese</p><p className="text-xl font-bold">{formatEuro(data.totale)}</p></CardContent></Card>
            <Card><CardContent className="p-4"><p className="text-xs text-slate-500">Ospiti attivi</p><p className="text-xl font-bold">{data.ospiti_attivi}</p></CardContent></Card>
            <Card className={data.anomalie ? "border-amber-300" : ""}><CardContent className="p-4"><p className="text-xs text-slate-500">Anomalie</p><p className="text-xl font-bold">{data.anomalie}</p></CardContent></Card>
          </div>
          <div className="rounded-lg border border-slate-200 overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-left text-slate-600"><tr><th className="p-3">Categoria</th><th className="p-3">Mese</th><th className="p-3">Media 6 mesi</th><th className="p-3">Scostamento</th><th className="p-3">Costo ospite/giorno</th></tr></thead>
              <tbody>{data.categorie.map((item) => (
                <tr key={item.categoria} className="border-t border-slate-100">
                  <td className="p-3 font-medium">{LABELS[item.categoria]}</td><td className="p-3">{formatEuro(item.totale)}</td><td className="p-3">{formatEuro(item.media_mobile_6_mesi)}</td>
                  <td className="p-3">{item.scostamento_percentuale == null ? "—" : <Badge variant="outline" className={item.anomalia ? "border-amber-300 bg-amber-50 text-amber-800" : ""}>{item.anomalia ? <AlertTriangle className="w-3 h-3 mr-1" /> : item.scostamento_percentuale > 0 ? <TrendingUp className="w-3 h-3 mr-1" /> : <TrendingDown className="w-3 h-3 mr-1" />}{item.scostamento_percentuale > 0 ? "+" : ""}{item.scostamento_percentuale}%</Badge>}</td>
                  <td className="p-3">{item.costo_per_ospite_giorno == null ? "—" : formatEuro(item.costo_per_ospite_giorno)}</td>
                </tr>
              ))}</tbody>
            </table>
          </div>
          {uncategorized.length > 0 && (
            <div className="rounded-lg border border-amber-200 bg-amber-50/40 p-4">
              <h3 className="font-medium text-slate-800">Fatture da categorizzare</h3>
              <p className="text-xs text-slate-600 mt-1 mb-3">La categoria verrà proposta anche per le fatture future dello stesso fornitore quando è disponibile la partita IVA.</p>
              <div className="space-y-2">
                {uncategorized.map((item) => (
                  <div key={item.id} className="flex flex-col sm:flex-row gap-2 sm:items-center text-sm">
                    <span className="flex-1 truncate">{item.fornitore || item.numero || "Fornitore"}</span>
                    <span className="text-slate-500">{formatEuro(item.totale)}</span>
                    <select className="rounded-md border border-slate-300 bg-white px-2 py-1.5" defaultValue="" onChange={(event) => event.target.value && setCategory(item, event.target.value)}>
                      <option value="" disabled>Assegna categoria</option>
                      {Object.entries(LABELS).map(([key, label]) => <option key={key} value={key}>{label}</option>)}
                    </select>
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
